import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { HiPlus, HiShoppingCart, HiXMark } from 'react-icons/hi2';
import { useCart } from '../../context/cart/CartContext';
import { CartService } from '../../service/manage/exam_subscription/cart.service';
import { AssessmentCreditService } from '../../service/manage/exam_subscription/assessCredit.service';
import { AssessmentCreditPackPriceService } from '../../service/manage/exam_subscription/assessCreditPackagePrice.service';
import { StudentAddressService } from '../../service/manage/registration/user.address.service';
import { CountryService } from '../../service/settings/manage/country.service';
import { ProvinceService } from '../../service/settings/manage/province.service';
import { CityService } from '../../service/settings/manage/city.service';
import AssessmentCreditCard from './AssessmentCreditCard';
import CreditPackageCard from './CreditPackageCard';
import './EnrollmentCart.css';
import { sweetSuccessAlert, sweetErrorAlert, sweetStatusAlert } from '../../components/common/SweetAlert';
import swal from 'sweetalert';
import './AccountSectionHeader.css';
import { routes } from '../../helper/routes';

const EnrollmentCart = () => {
    const history = useHistory();
    const location = useLocation();
    const { fetchCartItemCount } = useCart();

    // Data states
    const [purchasePapers, setPurchasePapers] = useState([]);
    const [creditPackages, setCreditPackages] = useState([]);
    const [packageQuantities, setPackageQuantities] = useState({});
    const [currencySymbol, setCurrencySymbol] = useState('LKR');
    const [cartId, setCartId] = useState(null);
    const [userType, setUserType] = useState(sessionStorage.getItem('userType'));

    // UI states
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState(null);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [itemToRemove, setItemToRemove] = useState(null);
    const [showSaveConfirmDialog, setShowSaveConfirmDialog] = useState(false);
    const [itemToSave, setItemToSave] = useState(null);

    // Credit limit states
    const [creditBuyLimit, setCreditBuyLimit] = useState(0);
    const [creditBuyLimitStatus, setCreditBuyLimitStatus] = useState(0);
    const [currentUserCredits, setCurrentUserCredits] = useState(0);
    
    // Multi-student credit limit states (for parents)
    const [studentCreditData, setStudentCreditData] = useState({}); 
    // Billing Address States
    const [addresses, setAddresses] = useState([]);
    const [selectedAddress, setSelectedAddress] = useState(null);
    const [showAddressModal, setShowAddressModal] = useState(false);
    const [addressFormData, setAddressFormData] = useState({
        address_line_1: '',
        address_line_2: '',
        city_id: '',
        province_id: '',
        postal_code: '',
        country_id: '',
    });
    const [loadingAddresses, setLoadingAddresses] = useState(false);
    const [countries, setCountries] = useState([]);
    const [provinces, setProvinces] = useState([]);
    const [cities, setCities] = useState([]);
    const [loadingCountries, setLoadingCountries] = useState(false);
    const [loadingProvinces, setLoadingProvinces] = useState(false);
    const [loadingCities, setLoadingCities] = useState(false); 

    // Reusable function to refresh cart data
    const loadCartData = async () => {
        setLoading(true);
        setError(null);

        try {
            const user_id = sessionStorage.getItem('manage_user_id');
            const res = await CartService.getAllCartItems(user_id);

            if (res.success) {
                const assessments = res.data.assessmentItems || [];
                const packages = res.data.creditPackageItems || [];

                if (res.data.cart && res.data.cart.cart_id) {
                    setCartId(res.data.cart.cart_id);
                }

                if (assessments.length > 0 && assessments[0].currency_symbol) {
                    setCurrencySymbol(assessments[0].currency_symbol);
                } else if (packages.length > 0 && packages[0].currency_symbol) {
                    setCurrencySymbol(packages[0].currency_symbol);
                }

                setPurchasePapers(assessments);
                setCreditPackages(packages);

                // Initialise quantities from API response
                const pkgqty = {};
                packages.forEach(p => { pkgqty[p.cart_item_id] = p.quantity || 1; });
                setPackageQuantities(pkgqty);
            } else {
                setError(res.message || 'Failed to load cart items.');
            }
        } catch (err) {
            setError(err.message || 'Failed to load cart items.');
        } finally {
            setLoading(false);
        }
    };

    const loadCartDataAfterPayment = async () => {
        setError(null);

        try {
            const user_id = sessionStorage.getItem('manage_user_id');
            const res = await CartService.getAllCartItems(user_id);

            if (res.success) {
                const assessments = res.data.assessmentItems || [];
                const packages = res.data.creditPackageItems || [];

                if (res.data.cart && res.data.cart.cart_id) {
                    setCartId(res.data.cart.cart_id);
                }

                if (assessments.length > 0 && assessments[0].currency_symbol) {
                    setCurrencySymbol(assessments[0].currency_symbol);
                } else if (packages.length > 0 && packages[0].currency_symbol) {
                    setCurrencySymbol(packages[0].currency_symbol);
                }

                setPurchasePapers(assessments);
                setCreditPackages(packages);

                const pkgqty = {};
                packages.forEach(p => { pkgqty[p.cart_item_id] = p.quantity || 1; });
                setPackageQuantities(pkgqty);
                
                if (packages.length > 0) {
                    fetchCreditLimitData();
                }
            } else {
                setError(res.message || 'Failed to load cart items.');
            }
        } catch (err) {
            setError(err.message || 'Failed to load cart items.');
        } finally {
            setLoading(false);
        }
    };

    // Handle payment gateway redirect responses
    const handlePaymentResponse = () => {
        const query = new URLSearchParams(window.location.search);
        const cancel = query.get('cancel');
        const timeout = query.get('timeout');

        if (cancel === 'true') {
            sessionStorage.removeItem('checkout_session_id');
            query.delete('cancel');
            loadCartDataAfterPayment();
            sweetStatusAlert('', 'You are back to your cart!', 'info');
            updateUrl(query);
            setLoading(false);
        } else if (timeout === 'true') {
            sessionStorage.removeItem('checkout_session_id');
            query.delete('timeout');
            loadCartDataAfterPayment();
            sweetStatusAlert('Timeout!', 'Your payment session timed out. Please try again.', 'warning');
            updateUrl(query);
            setLoading(false);
        } else {
            setLoading(false);
        }
    };

    const updateUrl = (query) => {
        const newQueryString = query.toString();
        const newUrl = newQueryString ? window.location.pathname + '?' + newQueryString : window.location.pathname;
        window.history.replaceState({}, document.title, newUrl);
    };

    // Load all cart data on component mount
    useEffect(() => {
        const query = new URLSearchParams(window.location.search);
        const hasPaymentResponse = query.get('success') || query.get('cancel') || query.get('timeout');
        
        if (hasPaymentResponse) {
            setLoading(true);
            handlePaymentResponse();
        } else {
            loadCartData();
        }
        
        // Fetch user addresses and countries
        fetchUserAddresses();
        fetchCountries();
    }, []);

    useEffect(() => {
        if (creditPackages.length > 0) {
            fetchCreditLimitData();
        }
    }, [creditPackages.length, userType, creditPackages]);

    const getStudentIdsFromCart = () => {
        if (!creditPackages || creditPackages.length === 0) return [];
        const studentIds = new Set();
        creditPackages.forEach(pkg => {
            if (pkg.user_id) {
                studentIds.add(pkg.user_id);
            }
        });
        return Array.from(studentIds);
    };

    const getPackagesByStudent = (studentId) => {
        return creditPackages.filter(pkg => pkg.user_id === studentId);
    };

    // Calculate total credits for a specific student in cart
    const getTotalCartCreditsPerStudent = (studentId) => {
        const packages = getPackagesByStudent(studentId);
        return packages.reduce((sum, pkg) => {
            const qty = packageQuantities[pkg.cart_item_id] || 1;
            return sum + (parseInt(pkg.credits) || 0) * qty;
        }, 0);
    };

    // Fetch credit buy limit setting and the user's current available credits
    const fetchCreditLimitData = async () => {
        try {
            const limitRes = await AssessmentCreditPackPriceService.getLimitOfBuyCreditPackages();
            if (limitRes?.data) {
                setCreditBuyLimit(limitRes.data.limit_count || 0);
                setCreditBuyLimitStatus(limitRes.data.status_id || 0);
            }

            // Handle parent users with multiple students
            if (userType === 'parent' && creditPackages.length > 0) {
                const studentIds = getStudentIdsFromCart();
                if (studentIds.length > 0) {
                    const creditsPromises = studentIds.map(sid =>
                        AssessmentCreditService.getAllExamCreditsByUser(100, sid)
                    );
                    const creditsResults = await Promise.all(creditsPromises);
                    
                    const creditDataMap = {};
                    studentIds.forEach((sid, index) => {
                        const creditsRes = creditsResults[index];
                        if (creditsRes?.success && creditsRes.data?.statistics) {
                            creditDataMap[sid] = {
                                limit: limitRes?.data?.limit_count || 0,
                                status: limitRes?.data?.status_id || 0,
                                current_credits: creditsRes.data.statistics.total_credits_you_can_use || 0,
                                student_name: creditPackages.find(p => p.user_id === sid)?.user_name || `Student ${sid}`
                            };
                        }
                    });
                    setStudentCreditData(creditDataMap);
                }
            } else {
                // Handle non-parent users
                const creditsRes = await AssessmentCreditService.getAllExamCreditsByUser(
                    100,
                    sessionStorage.getItem('manage_user_id')
                );
                if (creditsRes?.success && creditsRes.data?.statistics) {
                    setCurrentUserCredits(creditsRes.data.statistics.total_credits_you_can_use || 0);
                }
            }
        } catch (err) {
            console.error('Failed to fetch credit limit data:', err);
        }
    };

    // Fetch user addresses
    const fetchUserAddresses = async () => {
        try {
            setLoadingAddresses(true);
            const user_id = sessionStorage.getItem('manage_user_id');
            const res = await StudentAddressService.getUserAddressesByUserId(user_id);

            if (res.success && res.data) {
                setAddresses(res.data);
                if (res.data.length > 0) {
                    setSelectedAddress(res.data[0].address_id);
                }
            }
        } catch (err) {
            console.error('Failed to fetch user addresses:', err);
        } finally {
            setLoadingAddresses(false);
        }
    };

    // Fetch countries for dropdown
    const fetchCountries = async () => {
        try {
            setLoadingCountries(true);
            const res = await CountryService.getAll();
            if (res.success && res.data) {
                setCountries(res.data);
            }
        } catch (err) {
            console.error('Failed to fetch countries:', err);
        } finally {
            setLoadingCountries(false);
        }
    };

    // Fetch provinces for selected country
    const fetchProvinces = async (country_id) => {
        if (!country_id) {
            setProvinces([]);
            setCities([]);
            return;
        }

        try {
            setLoadingProvinces(true);
            const res = await ProvinceService.getProvinceForCountry(country_id);
            if (res.success && res.data) {
                setProvinces(res.data);
            }
        } catch (err) {
            console.error('Failed to fetch provinces:', err);
        } finally {
            setLoadingProvinces(false);
        }
    };

    // Fetch cities for selected province
    const fetchCities = async (province_id) => {
        if (!province_id) {
            setCities([]);
            return;
        }

        try {
            setLoadingCities(true);
            const res = await CityService.getCitiesForProvince(province_id);
            if (res.success && res.data) {
                setCities(res.data);
            }
        } catch (err) {
            console.error('Failed to fetch cities:', err);
        } finally {
            setLoadingCities(false);
        }
    };

    // Handle adding new address
    const handleAddAddress = async () => {
        const { address_line_1, postal_code, province_id, city_id, country_id } = addressFormData;
        if (!address_line_1 || !postal_code || !province_id || !city_id || !country_id) {
            sweetErrorAlert('Validation Error', 'Please fill in all required fields (Address Line 1, Country, Province/State, City, Postal Code)');
            return;
        }

        try {
            const user_id = sessionStorage.getItem('manage_user_id');
            const payload = [
                {
                    user_id: String(user_id),
                    address_1: String(addressFormData.address_line_1),
                    address_2: String(addressFormData.address_line_2),
                    postalcode: String(addressFormData.postal_code),
                    city_id: parseInt(addressFormData.city_id, 10),
                    province_id: parseInt(addressFormData.province_id, 10),
                    country_id: parseInt(addressFormData.country_id, 10),
                    status: 'Active',
                    created_by: String(user_id),
                }
            ];

            const res = await StudentAddressService.create(JSON.stringify(payload));

            if (res.success) {
                // Fetch updated addresses and select the newly added one
                const updatedRes = await StudentAddressService.getUserAddressesByUserId(user_id);
                if (updatedRes.success && updatedRes.data) {
                    setAddresses(updatedRes.data);
                    if (updatedRes.data.length > 0) {
                        setSelectedAddress(updatedRes.data[updatedRes.data.length - 1].address_id);
                    }
                }
                setShowAddressModal(false);
                setAddressFormData({
                    address_line_1: '',
                    address_line_2: '',
                    city_id: '',
                    province_id: '',
                    postal_code: '',
                    country_id: '',
                });
                setProvinces([]);
                setCities([]);
                sweetSuccessAlert('Success', 'Address added successfully and selected as billing address!');
            } else {
                sweetErrorAlert('Error', res.message || 'Failed to add address');
            }
        } catch (err) {
            console.error('Error adding address:', err);
            sweetErrorAlert('Error', 'Failed to add address. Please try again.');
        }
    };

    // Handle address form input change
    const handleAddressFormChange = (field, value) => {
        if (field === 'country_id') {
            setAddressFormData(prev => ({
                ...prev,
                [field]: value,
                province_id: '',
                city_id: ''
            }));
            fetchProvinces(value);
            setCities([]);
        } else if (field === 'province_id') {
            setAddressFormData(prev => ({
                ...prev,
                [field]: value,
                city_id: ''
            }));
            fetchCities(value);
        } else {
            setAddressFormData(prev => ({
                ...prev,
                [field]: value
            }));
        }
    };

    // Calculate total credits from credit packages currently in the cart
    const getTotalCartCredits = () => {
        return creditPackages.reduce((sum, pkg) => {
            const qty = packageQuantities[pkg.cart_item_id] || 1;
            return sum + (parseInt(pkg.credits) || 0) * qty;
        }, 0);
    };

    // Get exceeding packages for a specific student
    const getExceedingPackagesForStudent = (studentId) => {
        const data = studentCreditData[studentId];
        if (!data || data.status !== 1 || data.limit <= 0) return [];

        const packages = getPackagesByStudent(studentId);
        let runningTotal = data.current_credits;
        const exceeding = [];

        for (const pkg of packages) {
            const qty = packageQuantities[pkg.cart_item_id] || 1;
            const pkgCredits = (parseInt(pkg.credits) || 0) * qty;
            runningTotal += pkgCredits;
            if (runningTotal >= data.limit) {
                exceeding.push(pkg);
            }
        }

        return exceeding;
    };

    const getStudentsExceedingLimit = () => {
        if (userType !== 'parent' || Object.keys(studentCreditData).length === 0) return [];

        const exceeded = [];
        const studentIds = getStudentIdsFromCart();

        for (const studentId of studentIds) {
            const data = studentCreditData[studentId];
            if (!data || data.status !== 1 || data.limit <= 0) continue;

            const cartCredits = getTotalCartCreditsPerStudent(studentId);
            const projectedTotal = data.current_credits + cartCredits;

            if (projectedTotal >= data.limit) {
                const exceedingPkgs = getExceedingPackagesForStudent(studentId);
                exceeded.push({
                    student_id: studentId,
                    student_name: data.student_name,
                    current_credits: data.current_credits,
                    cart_credits: cartCredits,
                    projected_total: projectedTotal,
                    limit: data.limit,
                    exceeding_packages: exceedingPkgs
                });
            }
        }

        return exceeded;
    };

    // Get the list of credit packages that cause the limit to be exceeded
    const getExceedingPackages = () => {
        if (creditBuyLimitStatus !== 1 || creditBuyLimit <= 0 || creditPackages.length === 0) {
            return [];
        }

        let runningTotal = currentUserCredits;
        const exceeding = [];

        for (const pkg of creditPackages) {
            const qty = packageQuantities[pkg.cart_item_id] || 1;
            const pkgCredits = (parseInt(pkg.credits) || 0) * qty;
            runningTotal += pkgCredits;
            if (runningTotal >= creditBuyLimit) {
                exceeding.push(pkg);
            }
        }

        return exceeding;
    };

    // Show confirmation dialog before removing
    const confirmRemoveItem = (itemId, type = 'paper') => {
        setItemToRemove({ id: itemId, type });
        setShowConfirmDialog(true);
    };

    // For the API to remove item from cart
    const handleRemoveItem = async () => {
        setShowConfirmDialog(false);
        if (!itemToRemove) return;

        try {
            const { id: itemId, type } = itemToRemove;

            // Payload for the API
            let product_type, product_id, user_id;
            if (type === 'paper') {
                const paper = purchasePapers.find(item => item.cart_item_id === itemId);
                product_type = paper?.product_type || 'assessment';
                product_id = paper?.assessment_id;
                user_id = paper?.user_id;
            } else {
                const pkg = creditPackages.find(item => item.cart_item_id === itemId);
                product_type = pkg?.product_type || 'credit_package';
                product_id = pkg?.product_id;
                user_id = pkg?.user_id;
            }

            const created_by = sessionStorage.getItem('manage_user_id');
            const payload = { user_id, product_type, product_id, created_by };
            const res = await CartService.removeFromCart(
                JSON.stringify(payload)
            );

            if (res.success) {
                await loadCartData();
                if (userType === 'parent' && creditPackages.length > 0) {
                    fetchCreditLimitData();
                }
                fetchCartItemCount();
                sweetSuccessAlert('Removed!', 'Item removed from cart successfully.');
            } else {
                sweetErrorAlert('', res.message || 'Failed to remove item.');
            }
        } catch (err) {
            console.error('Error removing item:', err);
            sweetErrorAlert('', 'Failed to remove item. Please try again.');
        } finally {
            setShowConfirmDialog(false);
            setItemToRemove(null);
        }
    };

    const cancelRemove = () => {
        setShowConfirmDialog(false);
        setItemToRemove(null);
    };

    // Show confirmation dialog before saving
    const confirmSaveItem = (itemId, type = 'paper') => {
        setItemToSave({ id: itemId, type });
        setShowSaveConfirmDialog(true);
    };

    // Handle saving item for later (removes from cart and saves)
    const handleSaveItem = async () => {
        setShowSaveConfirmDialog(false);
        if (!itemToSave) return;

        try {
            const { id: itemId, type } = itemToSave;

            // Payload for the API
            let product_type, product_id, user_id;
            if (type === 'paper') {
                const paper = purchasePapers.find(item => item.cart_item_id === itemId);
                product_type = paper?.product_type || 'assessment';
                product_id = paper?.assessment_id;
                user_id = paper?.user_id;
            } else {
                const pkg = creditPackages.find(item => item.cart_item_id === itemId);
                product_type = pkg?.product_type || 'credit_package';
                product_id = pkg?.product_id;
                user_id = pkg?.user_id;
            }

            const created_by = sessionStorage.getItem('manage_user_id');
            const quantity = type === 'paper' ? 1 : (packageQuantities[itemId] || 1);
            const payload = { user_id, product_type, product_id, quantity, created_by };
            const res = await CartService.saveForLater(
                JSON.stringify(payload)
            );

            if (res.success) {
                await loadCartData();
                // Refresh credit limits if parent with packages
                if (userType === 'parent' && creditPackages.length > 0) {
                    fetchCreditLimitData();
                }
                fetchCartItemCount();
                sweetSuccessAlert('Saved!', 'Item saved for later successfully.');
            } else {
                sweetErrorAlert('', res.message || 'Failed to save item for later.');
            }
        } catch (err) {
            console.error('Error saving item:', err);
            sweetErrorAlert('', 'Failed to save item. Please try again.');
        } finally {
            setShowSaveConfirmDialog(false);
            setItemToSave(null);
        }
    };

    const cancelSave = () => {
        setShowSaveConfirmDialog(false);
        setItemToSave(null);
    };

    // Quantity helpers
    const handlePackageQtyChange = (id, qty) => {
        setPackageQuantities(prev => ({ ...prev, [id]: qty }));
    };

    const papersSubtotal = purchasePapers.reduce((sum, p) => sum + parseFloat(p.amount || 0) * 1, 0);
    const packagesSubtotal = creditPackages.reduce((sum, p) => sum + parseFloat(p.credit_amount_before_discount || 0) * (packageQuantities[p.cart_item_id] || 1), 0);
    const subtotal = papersSubtotal + packagesSubtotal;
    const totalDiscount = creditPackages.reduce((sum, p) => sum + parseFloat(p.discount_amount || 0) * (packageQuantities[p.cart_item_id] || 1), 0);
    const total = Math.max(0, subtotal - totalDiscount);

    // Handle cash-based purchase directly
    const handleBuyCheckout = async () => {
        if (purchasePapers.length === 0 && creditPackages.length === 0) return;

        if (userType === 'parent' && Object.keys(studentCreditData).length > 0) {
            const exceededStudents = getStudentsExceedingLimit();
            if (exceededStudents.length > 0) {
                const warnings = exceededStudents.map(student => {
                    const pkgNames = student.exceeding_packages
                        .map(p => p.name || `Package #${p.cart_item_id}`)
                        .join(', ');
                    return `${student.student_name}'s current credits ${student.current_credits} plus this new package credit ${student.cart_credits} (${student.projected_total} credits) exceed the allowed credit limit of ${student.limit}. Remove or save: ${pkgNames}`;
                }).join('\n\n');

                await swal({
                    title: "Credit Limit Exceeded",
                    text: `One or more students have exceeded their credit limit:\n\n${warnings}\n\nPlease remove or save excess packages for later.`,
                    icon: "warning",
                    buttons: {
                        cancel: "Go Back",
                    },
                    dangerMode: true,
                });
                return;
            }
        } else {
            // For non-parent users, Check credit limit
            if (creditBuyLimitStatus === 1 && creditBuyLimit > 0 && creditPackages.length > 0) {
                const totalCartCredits = getTotalCartCredits();
                const projectedTotal = currentUserCredits + totalCartCredits;

                if (projectedTotal >= creditBuyLimit) {
                    const exceedingPkgs = getExceedingPackages();
                    const pkgNames = exceedingPkgs.map(p => p.name || `Package #${p.cart_item_id}`).join(', ');

                    await swal({
                        title: "Credit Limit Exceeded",
                        text: `Your current credits (${currentUserCredits}) plus the cart credit packages (${totalCartCredits} credits) would reach ${projectedTotal} credits, exceeding the allowed limit of ${creditBuyLimit}. Please remove or save the following package(s) for later: ${pkgNames}`,
                        icon: "warning",
                        buttons: {
                            cancel: "Go Back",
                        },
                        dangerMode: true,
                    });
                    return;
                }
            }
        }

        const cart_items = [
            ...purchasePapers.map(p => ({
                cart_item_id: p.cart_item_id,
                quantity: 1,
            })),
            ...creditPackages.map(p => ({
                cart_item_id: p.cart_item_id,
                quantity: packageQuantities[p.cart_item_id] || 1,
            })),
        ];

        history.push({
            pathname: '/cart-summary-view',
            state: { cart_items, cart_id: cartId, address_id: selectedAddress },
        });
    };

    // Derive whether credit limit is exceeded for the UI warning
    const isCreditLimitExceeded = userType === 'parent'
        ? getStudentsExceedingLimit().length > 0
        : (creditBuyLimitStatus === 1
            && creditBuyLimit > 0
            && creditPackages.length > 0
            && (currentUserCredits + getTotalCartCredits()) >= creditBuyLimit);

    return (
        <div className="enrollment-cart-page">
            <div className="account-section-page-header">
                <div className="account-section-page-header-left">
                    <h1 className="account-section-page-title">My Cart</h1>
                    <p className="account-section-page-subtitle">
                        Review your saved purchases, adjust quantities, and confirm checkout when ready.
                    </p>
                </div>
            </div>

            <div className="cart-page-tabs-wrap">
                <div className="paper-tabs cart-shared-paper-tabs" role="tablist" aria-label="Cart navigation">
                    <button
                        type="button"
                        className={`MuiTab-root ${location.pathname === routes.my_cart ? 'Mui-selected' : ''}`}
                        onClick={() => history.push(routes.my_cart)}
                    >
                        My Cart
                    </button>
                    <button
                        type="button"
                        className={`MuiTab-root ${location.pathname === routes.saved_items ? 'Mui-selected' : ''}`}
                        onClick={() => history.push(routes.saved_items)}
                    >
                        Saved Items
                    </button>
                </div>
            </div>

            {error && (
                <div className="cart-error-message">
                    <p>{error}</p>
                </div>
            )}



            {/* Confirmation Dialog */}
            {showConfirmDialog && (
                <div className="cart-confirm-overlay">
                    <div className="cart-confirm-dialog">
                        <h3>Confirm Removal</h3>
                        <p>Are you sure you want to remove this item from your cart?</p>
                        <div className="cart-confirm-actions">
                            <button onClick={cancelRemove} className="cart-confirm-cancel">
                                Cancel
                            </button>
                            <button onClick={handleRemoveItem} className="cart-confirm-remove">
                                Remove
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Save Confirmation Dialog */}
            {showSaveConfirmDialog && (
                <div className="cart-confirm-overlay">
                    <div className="cart-confirm-dialog">
                        <h3>Save for Later</h3>
                        <p>Do you wish to remove this item from your cart and save it for later?</p>
                        <div className="cart-confirm-actions">
                            <button onClick={cancelSave} className="cart-confirm-cancel">
                                Cancel
                            </button>
                            <button onClick={handleSaveItem} className="cart-confirm-save">
                                Save for Later
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Address Modal */}
            {showAddressModal && (
                <div className="cart-confirm-overlay cart-address-modal-overlay">
                    <div className="cart-address-modal">
                        <h3 className="cart-address-modal-title">Add New Billing Address</h3>
                        <div className="cart-address-modal-body row g-3">
                            <div className="cart-address-field col-12 col-md-6">
                                <label className="cart-address-label">
                                    Address Line 1 <span className="cart-address-required">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={addressFormData.address_line_1}
                                    onChange={(e) => handleAddressFormChange('address_line_1', e.target.value)}
                                    placeholder="e.g., 123 Main Street"
                                    className="cart-address-control"
                                />
                            </div>

                            <div className="cart-address-field col-12 col-md-6">
                                <label className="cart-address-label">
                                    Address Line 2
                                </label>
                                <input
                                    type="text"
                                    value={addressFormData.address_line_2}
                                    onChange={(e) => handleAddressFormChange('address_line_2', e.target.value)}
                                    placeholder="e.g., Apartment, Suite"
                                    className="cart-address-control"
                                />
                            </div>

                            <div className="cart-address-field col-12 col-md-6">
                                <label className="cart-address-label">
                                    Country <span className="cart-address-required">*</span>
                                </label>
                                <select
                                    value={addressFormData.country_id}
                                    onChange={(e) => handleAddressFormChange('country_id', e.target.value)}
                                    className="cart-address-control cart-address-select"
                                >
                                    <option value="">Select Country</option>
                                    {loadingCountries ? (
                                        <option disabled>Loading countries...</option>
                                    ) : (
                                        countries.map((country) => (
                                            <option key={country.country_id} value={country.country_id}>
                                                {country.name}
                                            </option>
                                        ))
                                    )}
                                </select>
                            </div>

                            <div className="cart-address-field col-12 col-md-6">
                                <label className="cart-address-label">
                                    State/Province <span className="cart-address-required">*</span>
                                </label>
                                <select
                                    value={addressFormData.province_id}
                                    onChange={(e) => handleAddressFormChange('province_id', e.target.value)}
                                    disabled={!addressFormData.country_id}
                                    className="cart-address-control cart-address-select"
                                >
                                    <option value="">Select State/Province</option>
                                    {loadingProvinces ? (
                                        <option disabled>Loading provinces...</option>
                                    ) : (
                                        provinces.map((province) => (
                                            <option key={province.province_id} value={province.province_id}>
                                                {province.name}
                                            </option>
                                        ))
                                    )}
                                </select>
                            </div>

                            <div className="cart-address-field col-12 col-md-6">
                                <label className="cart-address-label">
                                    City <span className="cart-address-required">*</span>
                                </label>
                                <select
                                    value={addressFormData.city_id}
                                    onChange={(e) => handleAddressFormChange('city_id', e.target.value)}
                                    disabled={!addressFormData.province_id}
                                    className="cart-address-control cart-address-select"
                                >
                                    <option value="">Select City</option>
                                    {loadingCities ? (
                                        <option disabled>Loading cities...</option>
                                    ) : (
                                        cities.map((city) => (
                                            <option key={city.city_id} value={city.city_id}>
                                                {city.name}
                                            </option>
                                        ))
                                    )}
                                </select>
                            </div>

                            <div className="cart-address-field col-12 col-md-6">
                                <label className="cart-address-label">
                                    Postal Code <span className="cart-address-required">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={addressFormData.postal_code}
                                    onChange={(e) => handleAddressFormChange('postal_code', e.target.value)}
                                    placeholder="e.g., 10001"
                                    className="cart-address-control"
                                />
                            </div>
                        </div>

                        <div className="cart-confirm-actions cart-address-actions">
                            <button
                                onClick={() => {
                                    setShowAddressModal(false);
                                    setAddressFormData({
                                        address_line_1: '',
                                        address_line_2: '',
                                        city_id: '',
                                        province_id: '',
                                        postal_code: '',
                                        country_id: '',
                                    });
                                    setProvinces([]);
                                    setCities([]);
                                }}
                                className="cart-confirm-cancel"
                            >
                                <HiXMark className="cart-address-action-icon" />
                                Cancel
                            </button>
                            <button onClick={handleAddAddress} className="cart-address-submit-btn">
                                <HiPlus className="cart-address-action-icon" />
                                Add Address
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Loading State */}
            {loading ? (
                <div className="cart-loading-state">
                    <p>Loading cart items...</p>
                </div>
            ) : (
                <div className="cart-main-content container-fluid px-0">
                    {/* Buy Credits & Papers Section */}
                    <div className="row g-4 align-items-start">
                        <div className={(purchasePapers.length > 0 || creditPackages.length > 0) ? 'col-12 col-xl-7' : 'col-12'}>
                            <div className="cart-summary-card">
                        <h2 className="cart-section-title">Buy Credits & Papers</h2>

                        {/* Combined select-all for papers + packages */}

                        {purchasePapers.length === 0 && creditPackages.length === 0 ? (
                            <p className="cart-empty-message">No cart items.</p>
                        ) : (
                            <>
                                {/* Purchase Papers */}
                                {purchasePapers.length > 0 && (
                                    <div className="cart-sub-section">
                                        <div className="cart-items-list row g-3">
                                            {purchasePapers.map((paper) => (
                                                <div className="col-12" key={paper.cart_item_id}>
                                                    <AssessmentCreditCard
                                                        assessment={{
                                                            id: paper.cart_item_id,
                                                            title: paper.assess_name,
                                                            price: parseFloat(paper.amount || 0),
                                                            currencySymbol: paper.currency_symbol || currencySymbol,
                                                            user_code: paper.user_code,
                                                        }}
                                                        userType={userType}
                                                        onRemove={() => confirmRemoveItem(paper.cart_item_id, 'paper')}
                                                        onSave={() => confirmSaveItem(paper.cart_item_id, 'paper')}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Credit Packages */}
                                {creditPackages.length > 0 && (
                                    <div className="cart-sub-section">
                                        {/* Parent users: Show student warnings */}
                                        {(() => {
                                            if (userType === 'parent') {
                                                const exceededStudents = getStudentsExceedingLimit();
                                                if (exceededStudents.length > 0) {
                                                    return (
                                                        <div className="cart-credit-warning-group">
                                                            {exceededStudents.map((studentExceeded, idx) => {
                                                                const pkgNames = studentExceeded.exceeding_packages
                                                                    .map(p => p.name || `Package #${p.cart_item_id}`)
                                                                    .join(', ');
                                                                return (
                                                                    <p key={idx} className="cart-credit-warning">
                                                                        ⚠️ <strong>{studentExceeded.student_name}</strong>'s current credits {studentExceeded.current_credits} plus this new package credit {studentExceeded.cart_credits} ({studentExceeded.projected_total} credits) exceed the allowed credit limit of {studentExceeded.limit}. Remove or save: {pkgNames} to checkout
                                                                    </p>
                                                                );
                                                            })}
                                                        </div>
                                                    );
                                                }
                                            }
                                            return null;
                                        })()}
                                        
                                        {/* Show single warning */}
                                        {userType !== 'parent' && isCreditLimitExceeded && (
                                            <p className="cart-credit-warning">
                                                ⚠️ Your current credits ({currentUserCredits}) plus these packages ({getTotalCartCredits()} credits) exceed the allowed limit of {creditBuyLimit}. Remove or save excess packages to checkout.
                                            </p>
                                        )}
                                        
                                        <div className="cart-items-list row g-3">
                                            {creditPackages.map((pkg) => (
                                                <div className="col-12" key={pkg.cart_item_id}>
                                                    <CreditPackageCard
                                                        pkg={{
                                                            id: pkg.cart_item_id,
                                                            credits: pkg.credits,
                                                            name: pkg.name,
                                                            price: parseFloat(pkg.total_price || 0),
                                                            credits_expiry_duration: pkg.credits_expiry_duration,
                                                            credits_expiry_type: pkg.credits_expiry_type,
                                                            price_before_discount: parseFloat(pkg.credit_amount_before_discount),
                                                            discount: pkg.discount_rate ? `${pkg.discount_rate}%` : null,
                                                            currencySymbol: pkg.currency_symbol || currencySymbol,
                                                            user_code: pkg.user_code,
                                                        }}
                                                        userType={userType}
                                                        quantity={packageQuantities[pkg.cart_item_id] || 1}
                                                        onQuantityChange={(qty) => handlePackageQtyChange(pkg.cart_item_id, qty)}
                                                        onRemove={() => confirmRemoveItem(pkg.cart_item_id, 'package')}
                                                        onSave={() => confirmSaveItem(pkg.cart_item_id, 'package')}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}

                        {(purchasePapers.length > 0 || creditPackages.length > 0) && (
                            <>
                                {/* Cart Totals Summary */}
                                <div className="cart-totals">
                                    <hr className="cart-totals-divider" />
                                    <div className="cart-totals-row">
                                        <span className="cart-totals-label">Subtotal</span>
                                        <span className="cart-totals-value">{currencySymbol} {subtotal.toFixed(2)}</span>
                                    </div>
                                    <hr className="cart-totals-divider" />
                                    {totalDiscount > 0 && (
                                        <>
                                            <div className="cart-totals-row cart-totals-discount">
                                                <span className="cart-totals-label">Package Discount</span>
                                                <span className="cart-totals-value cart-totals-discount-value">− {currencySymbol} {totalDiscount.toFixed(2)}</span>
                                            </div>
                                            <hr className="cart-totals-divider" />
                                        </>
                                    )}
                                    <div className="cart-totals-row cart-totals-total">
                                        <span className="cart-totals-label cart-totals-total-label">Total</span>
                                        <span className="cart-totals-value cart-totals-total-value">{currencySymbol} {total.toFixed(2)}</span>
                                    </div>
                                </div>
                            </>
                        )}
                            </div>
                        </div>

                        {(purchasePapers.length > 0 || creditPackages.length > 0) && (
                            <div className="col-12 col-xl-5">
                                <div className="cart-checkout-billing">
                                    {/* Billing Address Section */}
                                    <div className="billing-address-section">
                                    <h3 className="billing-address-title">Billing Address</h3>
                                    
                                    {loadingAddresses ? (
                                        <p className="billing-address-loading">Loading addresses...</p>
                                    ) : (
                                        <>
                                            {addresses.length === 0 ? (
                                                <div className="billing-address-empty-state">
                                                    <p className="billing-address-empty-copy">No addresses found. Please add one.</p>
                                                    <button
                                                        onClick={() => setShowAddressModal(true)}
                                                        className="billing-address-action-btn"
                                                    >
                                                        + Add Address
                                                    </button>
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="addresses-grid row g-3 billing-address-grid">
                                                        {addresses.map((addr) => (
                                                            <div className="col-12" key={addr.address_id}>
                                                                <div
                                                                    className={`address-card ${selectedAddress === addr.address_id ? 'selected' : ''}`}
                                                                    onClick={() => setSelectedAddress(addr.address_id)}
                                                                >
                                                                    <div className="address-card-row">
                                                                        <input
                                                                            type="radio"
                                                                            name="address"
                                                                            checked={selectedAddress === addr.address_id}
                                                                            onChange={() => setSelectedAddress(addr.address_id)}
                                                                            className="address-card-radio"
                                                                        />
                                                                        <div className="address-card-copy">
                                                                            <p className="address-card-copy-text">
                                                                                {addr.full_address}
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                    <button
                                                        onClick={() => setShowAddressModal(true)}
                                                        className="billing-address-action-btn billing-address-secondary-action-btn"
                                                    >
                                                        + Add New Address
                                                    </button>
                                                </>
                                            )}
                                        </>
                                    )}
                                    </div>

                                    <button
                                        onClick={handleBuyCheckout}
                                        disabled={processing || isCreditLimitExceeded || !selectedAddress}
                                        className={`cart-confirm-btn ${(processing || isCreditLimitExceeded || !selectedAddress) ? 'cart-confirm-disabled' : ''}`}
                                    >
                                        <HiShoppingCart className="cart-confirm-icon" />
                                        {processing ? 'Processing...' : 'Confirm Order'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default EnrollmentCart;

